/**
 * El newsletter de un proyecto, del lado del navegador.
 *
 * Los tipos copian la forma del server tal cual. En particular `audiencia`
 * puede ser `null`: un proyecto sin lista no es un error ni una lista vacía,
 * y el panel tiene que poder decir "este proyecto todavía no tiene newsletter"
 * sin inventarse un cero.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

// ── Espejo de packages/server/src/services/newsletter/ ───────────────────────

/** Claves neutras (REGLA #6). La traducción ocurre en pantalla. */
export type EstadoMiembro = 'subscribed' | 'unsubscribed' | 'bounced' | 'complained';

export interface AudienciaDelPanel {
  id: number;
  name: string;
  fromAddress: string;
  replyTo: string | null;
  autoSend: boolean;
  autoSendEnabledAt: string | null;
  autoSendEnabledBy: string | null;
}

export interface ConteoAudiencia {
  suscritos: number;
  deBaja: number;
  rebotados: number;
  quejas: number;
}

export interface EnvioResumido {
  id: number;
  articleId: number;
  status: string;
  subject: string;
  destinatarios: number;
  fallidos: number;
  /** Personas distintas, no eventos: cada una cuenta una vez. */
  aperturas: number;
  clics: number;
  sentAt: string | null;
  createdAt: string;
}

export interface EstadoNewsletter {
  audiencia: AudienciaDelPanel | null;
  conteo: ConteoAudiencia | null;
  envios: EnvioResumido[];
}

export interface MiembroDelPanel {
  id: number;
  email: string;
  name: string | null;
  status: EstadoMiembro;
  consentSource: string | null;
  unsubscribedAt: string | null;
}

export type MotivoSinEnviar = 'sin_publicar' | 'anterior_al_automatico' | 'automatico_apagado';

export interface ArticuloParaEnviar {
  id: number;
  title: string;
  status: string;
  publishedPath: string | null;
  publishedAt: string | null;
  scheduledFor: string | null;
  envio: { id: number; status: string; entregados: number; fallidos: number; sentAt: string | null } | null;
  /** Por qué no va a salir solo. Null = sale, o ya salió. */
  motivoSinEnviar: MotivoSinEnviar | null;
}

export interface ResumenImportacion {
  agregados: number;
  repetidos: number;
  deBaja: number;
  invalidos: { fila: number; valor: string }[];
}

export interface ResultadoEnvio {
  sendId: number;
  entregados: number;
  fallidos: number;
  destinatarios: number;
}

// Con `/api` delante: el cliente de lib/api.ts NO lo antepone. Sin esto el
// navegador pedía /newsletter/23, el servidor devolvía el index.html de la SPA
// y el panel concluía que el proyecto no tenía lista teniendo 217 correos.
const base = (projectId: number): string => `/api/newsletter/${projectId}`;

export interface OrigenDisponible {
  /** El valor crudo de `contacts.source`. Clave, no etiqueta. */
  origen: string;
  /** Cuántos contactos de ese origen tienen un correo utilizable. */
  conCorreo: number;
}

export const clavesNewsletter = {
  estado: (projectId: number) => ['newsletter', projectId] as const,
  miembros: (projectId: number, estado?: string) => ['newsletter', projectId, 'miembros', estado ?? 'todos'] as const,
  articulos: (projectId: number) => ['newsletter', projectId, 'articulos'] as const,
  origenes: (projectId: number) => ['newsletter', projectId, 'origenes'] as const,
};

export function useNewsletter(projectId: number, habilitado = true) {
  return useQuery<EstadoNewsletter>({
    queryKey: clavesNewsletter.estado(projectId),
    queryFn: () => api.get(base(projectId)),
    enabled: habilitado && Number.isInteger(projectId),
  });
}

export function useMiembrosDelNewsletter(projectId: number, estado?: EstadoMiembro, habilitado = true) {
  return useQuery<{ miembros: MiembroDelPanel[] }>({
    queryKey: clavesNewsletter.miembros(projectId, estado),
    queryFn: () => api.get(`${base(projectId)}/miembros${estado ? `?estado=${estado}` : ''}`),
    enabled: habilitado && Number.isInteger(projectId),
  });
}

export function useArticulosDelNewsletter(projectId: number, habilitado = true) {
  return useQuery<{ articulos: ArticuloParaEnviar[] }>({
    queryKey: clavesNewsletter.articulos(projectId),
    queryFn: () => api.get(`${base(projectId)}/articulos`),
    enabled: habilitado && Number.isInteger(projectId),
  });
}

/**
 * Enciende o apaga el envío automático.
 *
 * `confirmo` solo viaja al encender, y el server lo exige: encender no
 * autoriza un correo, autoriza todos los que vengan. Apagar no pide nada,
 * porque frenar siempre es seguro.
 */
export function useFijarEnvioAutomatico(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (encendido: boolean) =>
      api.put<{ autoSend: boolean; autoSendEnabledAt: string | null; autoSendEnabledBy: string | null }>(
        `${base(projectId)}/automatico`,
        encendido ? { encendido: true, confirmo: true } : { encendido: false },
      ),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: clavesNewsletter.estado(projectId) }); },
  });
}

export interface EntradaImportacion {
  filas: { email: string; nombre?: string | null }[];
  procedencia: string;
  nombreLista?: string;
  remitente?: string;
  responderA?: string;
}

export function useImportarLista(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entrada: EntradaImportacion) =>
      api.post<{ resumen: ResumenImportacion; conteo: ConteoAudiencia }>(`${base(projectId)}/importar`, entrada),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['newsletter', projectId] }); },
  });
}

/** Manda un artículo a toda la lista. `confirmoDestinatarios` tiene que ser el número exacto. */
export function useEnviarArticulo(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { articleId: number; confirmoDestinatarios: number }) =>
      api.post<ResultadoEnvio>(`${base(projectId)}/enviar`, v),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['newsletter', projectId] }); },
  });
}

/** Los orígenes del CRM, para armar la lista sin pasar por un archivo. */
export function useOrigenesDeContactos(projectId: number, habilitado = true) {
  return useQuery<{ origenes: OrigenDisponible[] }>({
    queryKey: clavesNewsletter.origenes(projectId),
    queryFn: () => api.get(`${base(projectId)}/origenes`),
    enabled: habilitado && Number.isInteger(projectId),
  });
}

export interface EntradaDesdeContactos {
  origenes: string[];
  procedencia: string;
  nombreLista?: string;
  remitente?: string;
  responderA?: string;
}

export function useImportarDesdeContactos(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entrada: EntradaDesdeContactos) =>
      api.post<{ resumen: ResumenImportacion; conteo: ConteoAudiencia }>(
        `${base(projectId)}/importar-contactos`, entrada,
      ),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['newsletter', projectId] }); },
  });
}
